import { useEventInfo } from '@shared/utils/eventInfo';
import { useEventFeatureFlags } from './useEventFeatureFlags/useEventFeatureFlags';

/**
 * Map of paid-only features gated by event payment state.
 */
export type PaidFeature = 'websiteVideoHeader' | 'aiTextEditor';

type FeatureEvaluatorArgs = Readonly<{
  manuallyPaidEvent: boolean;
  websitePremiumVideoBackgroundEnabled: boolean;
}>;

const featureEvaluators: Readonly<Record<PaidFeature, (args: FeatureEvaluatorArgs) => boolean>> = {
  websiteVideoHeader: ({ manuallyPaidEvent, websitePremiumVideoBackgroundEnabled }) => manuallyPaidEvent || websitePremiumVideoBackgroundEnabled,
  aiTextEditor: ({ manuallyPaidEvent }) => manuallyPaidEvent,
};

const evaluateFeature = (
  feature: PaidFeature,
  eventFlags: Readonly<{
    manuallyPaidEvent: boolean;
    websitePremiumVideoBackgroundEnabled: boolean;
  }>,
) => {
  const evaluator = featureEvaluators[feature];

  if (!evaluator) {
    return false;
  }

  return evaluator({
    manuallyPaidEvent: eventFlags.manuallyPaidEvent,
    websitePremiumVideoBackgroundEnabled: eventFlags.websitePremiumVideoBackgroundEnabled,
  });
};

/**
 * Returns whether the requested paid feature is enabled for the current event.
 * It checks the event feature flags to see if the event is manually paid and
 * evaluates the feature-specific guard accordingly.
 */
export const usePaidFeature = (feature: PaidFeature) => {
  const { eventHandle } = useEventInfo();
  const { loadingEventFeatureFlags, isEventFeatureFlagEnabled } = useEventFeatureFlags({ eventHandle: eventHandle ?? '', skip: !eventHandle });

  const manuallyPaidEvent = loadingEventFeatureFlags ? false : (isEventFeatureFlagEnabled?.('manuallyPaidEvent') ?? false);
  const websitePremiumVideoBackgroundEnabled = loadingEventFeatureFlags ? false : (isEventFeatureFlagEnabled?.('websitePremiumVideoBackgroundEnabled') ?? false);

  return {
    value: evaluateFeature(feature, { manuallyPaidEvent, websitePremiumVideoBackgroundEnabled }),
    isLoading: loadingEventFeatureFlags,
  };
};
